import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const BarChartIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const HammerIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 12-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 0 1 0-3L12 9" />
    <path d="M17.64 15 22 10.64" />
    <path d="m20.91 11.7-1.25-1.25c-.6-.6-.93-1.4-.93-2.25V7.86c0-.55-.45-1-1-1H14.5c-.85 0-1.65-.33-2.25-.93L11 4.64" />
  </svg>
);

const ConstructionQuotingSoftware = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Structured data for Construction page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product", 
    "name": "Construction Quoting Software by QuoteTier",
    "description": "Best quoting software for builders, general contractors, and remodelers. Create interactive Good/Better/Best estimates that win more jobs.",
    "category": "Software",
    "brand": {
      "@type": "Brand",
      "name": "QuoteTier"
    },
    "offers": {
      "@type": "Offer",
      "name": "Free Trial",
      "price": "0",
      "priceCurrency": "USD"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "General Contractors, Builders, Remodelers"
    }
  };

  useEffect(() => {
    analytics.track('Construction Landing Page View');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Construction Quoting Software — Win More Bids with Tiered Pricing Options"
        description="Contractors using Good/Better/Best quotes win more bids and increase average project value. Present material upgrades, add-ons, and warranty options clients can choose themselves."
        canonical="/construction-quoting-software"
        structuredData={structuredData}
        ogTitle="Construction Quoting Software — Win More Bids"
        ogDescription="Stop sending static PDF estimates. Use QuoteTier to give your construction clients interactive options, clear allowances, and digital sign-offs."
      />
      {/* Header */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-32 md:py-40 lg:py-48">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
                Construction Quoting Software That <span className="text-primary font-bold">Upsells For You</span>
              </h1>
              <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
                The best quoting software for builders, remodelers, and trades. Give clients Good/Better/Best options for decks, roofs, and renovations in one interactive quote.
              </p>
              <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
                <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'construction-hero', action: 'signup' })}>
                  <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                    Start Free — 3 Quotes/Mo
                  </Button>
                </Link>
                <Link to="/demo-quote/construction" onClick={() => analytics.track('Demo Clicked', { location: 'construction-hero', industry: 'construction' })}>
                  <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline">
                    See Builder Quote Demo →
                  </Button>
                </Link>
              </div>
              <p className="font-jetbrains mt-10 text-lg text-gray-500">
                No credit card required • Setup in 5 minutes • Works on mobile
              </p>
            </div>
          </div>
        </section>

        {/* Construction Value Props */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Why Builders Choose QuoteTier</h2>
              <p className="font-jetbrains text-lg text-gray-600">Designed for general contractors, deck builders, and roofing companies.</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Good/Better/Best Packages</CardTitle>
                  <CardDescription className="font-jetbrains">Stop bidding the bare minimum. Show clients a standard build, a premium upgrade, and a luxury option side-by-side.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Ideal for decks (Pine vs. Composite vs. PVC)</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Perfect for roofing (3-Tab vs. Architectural vs. Premium)</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Clear Material Allowances</CardTitle>
                  <CardDescription className="font-jetbrains">Avoid disputes by clearly listing allowances for finishes, fixtures, and materials directly in the quote.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Set clear budget limits for clients</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Prevent "scope creep" before it starts</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Interactive Upsells</CardTitle>
                  <CardDescription className="font-jetbrains">Let clients self-select upgrades like premium trim, better insulation, or high-end fixtures.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Increase profit margins without "selling"</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Clients feel in control of the budget</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Mobile-First Quotes</CardTitle>
                  <CardDescription className="font-jetbrains">Send quotes that look amazing on any device. Clients can review and sign directly from their phone.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Instant digital signatures</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Track when they view the quote</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Perfect For Every Trade</h2>
              <p className="font-jetbrains text-lg text-gray-600">Flexible enough for any construction project</p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="text-center">
                <div className="mx-auto bg-black text-white w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <HammerIcon className="h-6 w-6" />
                </div>
                <h3 className="font-instrument text-xl font-bold mb-2">Deck Quoting Software</h3>
                <p className="font-jetbrains text-gray-600 text-sm">
                  Quote Pressure Treated, Cedar, and Composite options. Upsell lighting packages and custom railings.
                </p>
              </div>
              <div className="text-center">
                <div className="mx-auto bg-black text-white w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <ZapIcon className="h-6 w-6" />
                </div>
                <h3 className="font-instrument text-xl font-bold mb-2">Roof Quoting Software</h3>
                <p className="font-jetbrains text-gray-600 text-sm">
                  Offer Good (25-year), Better (30-year), and Best (50-year/Impact Resistant) shingle packages instantly.
                </p>
              </div>
              <div className="text-center">
                <div className="mx-auto bg-black text-white w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <BarChartIcon className="h-6 w-6" />
                </div>
                <h3 className="font-instrument text-xl font-bold mb-2">Remodel Estimating</h3>
                <p className="font-jetbrains text-gray-600 text-sm">
                  Kitchen and bath remodels with clear allowances for tile, cabinets, and countertops.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                QuoteTier vs Excel/Word
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                Stop wasting time with spreadsheets and documents
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">
                      Feature
                    </th>
                    <th className="text-center py-6 px-6 bg-black text-white">
                      <div className="flex flex-col items-center gap-2">
                        <span className="font-instrument text-lg font-bold">QuoteTier</span>
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      Excel / Word
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Interactive Options for Clients</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Instant Digital Signatures</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Real-time "Viewed" Notifications</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Mobile Friendly</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-gray-900 to-black text-white">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-4xl md:text-5xl font-bold mb-6">
              Ready to Win More Construction Jobs?
            </h2>
            <p className="font-jetbrains text-xl text-gray-200 mb-8">
              Join builders and contractors who are saving time and increasing profits with QuoteTier.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'construction-final-cta', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <Link to="/demo-quote/construction" onClick={() => analytics.track('Demo Clicked', { location: 'construction-final-cta', industry: 'construction' })}>
                <Button className="px-8 py-4 text-lg font-semibold border-2 border-white text-white hover:bg-white/10 rounded-lg" variant="outline">
                  Try Interactive Demo First →
                </Button>
              </Link>
            </div>
            
            <p className="font-jetbrains text-sm text-gray-300">
              No credit card required • Cancel anytime • Setup in minutes
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ConstructionQuotingSoftware;
